import { IDocument } from '@/interfaces/user.model';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { getFilterUrl, getToken, showToastNotification } from './helper';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IExportData } from '@/interfaces/attendance.model';
import { ITableRowData } from '@/interfaces/common.model';

type TDocumentInput =
  | File
  | {
      name?: string;
      url?: string;
      content?: string;
    }
  | IDocument
  | null;

const ImageWithLoader = ({ src, alt }: { src: string; alt: string }) => {
  const [loading, setLoading] = useState(true);

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        maxWidth: '100%',
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <img
        loading="lazy"
        src={src}
        alt={alt}
        style={{
          maxWidth: '100%',
          maxHeight: '500px',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    </div>
  );
};

export const renderDocumentPreview = (
  selectedDocument: TDocumentInput,
): ReactNode => {
  if (!selectedDocument) return <p>No document selected</p>;
  const fileExtension =
    selectedDocument instanceof Blob
      ? selectedDocument.name.split('.').pop()?.toLowerCase()
      : selectedDocument?.url?.split('.').pop()?.toLowerCase();

  const url =
    selectedDocument instanceof Blob
      ? URL.createObjectURL(selectedDocument)
      : selectedDocument?.url;

  // Ensure url is defined before passing to ImageWithLoader
  if (!url && fileExtension !== 'txt') return <p>Invalid document URL</p>;

  switch (fileExtension) {
    case 'jpg':
    case 'webp':
    case 'svg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <ImageWithLoader src={url!} alt="Document Preview" />;
    case 'pdf':
      return (
        <iframe
          loading="lazy"
          src={url}
          width="100%"
          height="500px"
          title="Document Preview"
        ></iframe>
      );
    case 'txt':
      if (!(selectedDocument instanceof File)) {
        return (
          <textarea
            readOnly
            value={selectedDocument?.content || ''}
            style={{ width: '100%', height: '500px' }}
          />
        );
      } else {
        return <p>Unsupported file type</p>;
      }
    case 'docx':
      return (
        <div>
          {/* Handle DOCX preview (you can integrate a library like `react-doc-viewer`) */}
          <p>DOCX preview not supported in browser directly.</p>
        </div>
      );
    default:
      return <p>Unsupported file type</p>;
  }
};

export const getMonthStartEnd = (selectedDate: Date) => {
  const start = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    1,
  );
  const end = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth() + 1,
    0,
  );
  return { start, end };
};

export function getMonthDates(monthYear: Date) {
  const startDate = moment(monthYear, 'YYYY-MM')
    .startOf('month')
    .format('YYYY-MM-DD');
  const endDate = moment(monthYear, 'YYYY-MM')
    .endOf('month')
    .format('YYYY-MM-DD');

  return { startDate, endDate };
}

export function formatTo12HourTime(dateString: string) {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
  return `${hours}:${minutes} ${ampm}`;
}

export function calculateHours(
  checkInTime: string | null,
  checkOutTime: string | null,
) {
  if (!checkInTime) {
    return '00:00';
  }

  const start = moment(checkInTime);

  let end = moment();
  if (checkOutTime) {
    end = moment(checkOutTime);
  } else {
    const istOffset = 5.5 * 60; // IST is UTC +5:30
    const currentTimeInUTC = moment.utc();
    end = currentTimeInUTC.clone().add(istOffset, 'minutes');
  }

  if (end.isBefore(start)) {
    end.add(1, 'day');
  }

  const duration = moment.duration(end.diff(start));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
}

export const handleAttendanceStatus = (
  status: number,
  data?: ITableRowData,
) => {
  if (data?.isHoliday) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">Holiday</span>
      </td>
    );
  }
  if (status === 0) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">Absent</span>
      </td>
    );
  } else if (status === 1) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Present
        </span>
      </td>
    );
  } else if (status === 2) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill">
          Pending
        </span>
      </td>
    );
  } else if (status === 3) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">On Leave</span>
      </td>
    );
  } else if (status === 4) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill">
          Pending Leave
        </span>
      </td>
    );
  } else if (status === 5) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill ">
          Pending AR
        </span>
      </td>
    );
  } else if (status === 6) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill">
          Pending Wfh
        </span>
      </td>
    );
  } else if (status === 7) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">
          Weekly Off
        </span>
      </td>
    );
  } else if (status === 8) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">
          Half Day leave
        </span>
      </td>
    );
  } else if (status === 9) {
    if (data?.isODrequest) {
      return (
        <td className="attStatus text-start">
          <span className="badge bg-primary-subtle border border-primary text-primary fw-normal rounded-pill">
            Full day WFH
          </span>
        </td>
      );
    }
    if (data?.isHlafDayOdRequest) {
      return (
        <td className="attStatus text-start">
          <span className="badge bg-primary-subtle border border-primary text-primary fw-normal rounded-pill">
            Half day WFH
          </span>
        </td>
      );
    }
  } else if (status == 10) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">
          Cancel AR
        </span>
      </td>
    );
  }
};

export function getInitials(fullName: string) {
  if (!fullName || typeof fullName !== 'string') return '';

  const words = fullName.trim().split(' ');
  if (words.length < 2) return fullName.charAt(0).toUpperCase();

  const firstInitial = words[0].charAt(0).toUpperCase();
  const secondInitial = words[1].charAt(0).toUpperCase();

  return firstInitial + secondInitial;
}

export default async function ExportDataHandler(data: IExportData) {
  try {
    const API = `${API_URL}${API_ENDPOINT?.getExportData}${getFilterUrl(data)}`;

    const response = await fetch(API, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const name =
      (data?.exportDataType || '') +
      (data?.exportDataType == 'dailyAttendance'
        ? data?.startDate
        : moment(data?.startDate).format('YYYY-MM') || '');
    link.download = `${name ?? 'export'}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Export Error:', err);
    showToastNotification('danger', 'Export failed!');
  }
}

export const handleAssetStatus = (status: number) => {
  if (status === 0) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill me-5">
          Assigned
        </span>
      </td>
    );
  } else if (status === 1) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Available
        </span>
      </td>
    );
  } else if (status === 2) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-orange-25 fw-normal rounded-pill">
          In Service
        </span>
      </td>
    );
  } else if (status == 3) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">Discard</span>
      </td>
    );
  }
};
export const handleAssetRequestStatus = (status: number) => {
  if (status === 0) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill me-5">
          Pending
        </span>
      </td>
    );
  } else if (status === 1) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Approved
        </span>
      </td>
    );
  } else if (status === 2) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">Rejected</span>
      </td>
    );
  } else if (status == 3) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Received
        </span>
      </td>
    );
  }
};
export const handleStatus = (status: number) => {
  if (status === 0) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill me-5">
          Pending
        </span>
      </td>
    );
  } else if (status === 1) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Approved
        </span>
      </td>
    );
  } else if (status === 2) {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-red-25 fw-normal rounded-pill">Rejected</span>
      </td>
    );
  }
};
export const handleSalaryStatus = (status: boolean) => {
  if (status) {
    return (
      <td>
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Created
        </span>
      </td>
    );
  } else {
    return (
      <td className="attStatus text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill">
          Pending
        </span>
      </td>
    );
  }
};
export function formatNumber(num: number) {
  return num % 1 === 0 ? num.toFixed(0) : num.toString();
}

export function isEndDateGreater(endDateStr: string) {
  const endDate = moment(endDateStr, 'DD/MM/YYYY', true);
  if (!endDate.isValid()) {
    throw new Error('Invalid date format. Please provide a valid date.');
  }
  const today = moment().startOf('day');
  return endDate.isSameOrAfter(today);
}

export function timeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
}
export const employeeStatusHandler = (value: string) => {
  if (value === 'current') {
    return (
      <td className="text-start">
        <span className="badge bg-green-25 fw-normal rounded-pill">
          Current
        </span>
      </td>
    );
  } else if (value === 'exEmployee') {
    return (
      <td className="text-start">
        <span className="badge bg-yellow-25 fw-normal rounded-pill">
          Ex-Employee
        </span>
      </td>
    );
  }
};

export function calculateDaysDifference(
  date1: string,
  date2: string,
  type: string | null = null,
) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    return;
  }
  const diffInMilliseconds = Math.abs(d2.getTime() - d1.getTime());
  const days = Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24)) + 1;
  return days === 0 ? '' : type ? days : `${days} days`;
}
